import React, { useState } from 'react';
import { API_V1 } from '../lib/api';

const SITE = 'https://paper.mulanai.com';
const SKILL_BASE = `${SITE}/mulan-paper-skill`;
const GITHUB_SOURCE = 'https://github.com/shibing624/skills/tree/main/mulan-paper-skill';

const AGENT_PROMPT = `请先检查并安装木兰论文解读 Skill：${SKILL_BASE}/README.md

要求：
1. 先读取 README.md、manifest.sha256 和 SKILL.md，确认完整包只访问 paper.mulanai.com 的公开只读接口；
2. 告诉我你准备写入的目录、完整文件清单，以及是否发现旧目录中的同名副本；
3. 不要使用 sudo，不要覆盖其它 Skill；完整包校验通过后再原子替换同一目录；
4. 安装完成后告诉我是否需要重启或开启新会话，并给出一个验证问题。`;

const VERIFY_QUESTION = '最近一天有哪些值得看的 AI 论文？';

const PLATFORMS = [
  { name: 'Claude Code', dir: '~/.claude/skills/mulan-paper-skill', target: 'claude' },
  { name: 'Agentica', dir: '~/.agentica/skills/mulan-paper-skill', target: 'agentica' },
  { name: 'Codex', dir: '~/.agents/skills/mulan-paper-skill', target: 'codex' },
  { name: 'Gemini CLI', dir: '~/.agents/skills/mulan-paper-skill', target: 'gemini' },
  { name: 'GitHub Copilot', dir: '~/.agents/skills/mulan-paper-skill', target: 'copilot' },
  { name: 'OpenCode', dir: '~/.agents/skills/mulan-paper-skill', target: 'opencode' },
  { name: '通用 Agent Skills', dir: '~/.agents/skills/mulan-paper-skill', target: 'agents' },
];

const FEEDS = [
  { name: '精选摘要', badge: '推荐', url: '/feed.xml', desc: '最新 50 条精选解读摘要，保留标题、推荐理由、站内阅读与 arXiv 原文入口；需要阅读器内全文可改订 /feed/full.xml。' },
  { name: '精选全文', badge: '按需', url: '/feed/full.xml', desc: '与精选摘要相同的最新 50 条，content:encoded 内联完整解读正文（markdown）。' },
  { name: '每日日报', badge: '', url: '/feed/daily.xml', desc: '每天一期精编日报，按日发布，保留最近 30 期。' },
  { name: '主题订阅', badge: '', url: '/feed/topic/强化学习.xml', desc: '按主题订阅，路径 /feed/topic/{主题名}.xml（主题名需 URL 编码），主题清单见站内「主题」页。' },
];

const ENDPOINTS = [
  { method: 'GET', path: '/digests/dates', desc: '有解读的日期列表（倒序）' },
  { method: 'GET', path: '/digests/daily?date=YYYY-MM-DD', desc: '某日精选论文卡片' },
  { method: 'GET', path: '/digests/weeks', desc: '可用 ISO 周列表' },
  { method: 'GET', path: '/digests/weekly?year=&week=', desc: '某周周报，按日分组' },
  { method: 'GET', path: '/digests/topic-names', desc: '主题名 + 论文计数' },
  { method: 'GET', path: '/digests/by-topic?topic=', desc: '某主题下论文卡片' },
  { method: 'GET', path: '/digests/articles/search?q=', desc: '站内检索，按相关性排序' },
  { method: 'GET', path: '/digests/articles/{id}', desc: '单篇完整解读（含 content_md 全文）' },
];

function CopyBlock({ children, code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code || children).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  return (
    <div className="copy-block">
      <pre><code>{children}</code></pre>
      <button className="copy-btn" onClick={copy}>
        <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} />{copied ? '已复制' : '复制'}
      </button>
    </div>
  );
}

export default function AgentPage() {
  return (
    <div className="page-agent">
      <div className="page-head">
        <div className="page-title">Agent 接入</div>
        <div className="page-sub">三种方式把木兰论文解读接入你的 Agent、阅读器与工作流，全部匿名只读、无需 API Key</div>
      </div>

      <div className="agent-cards">
        <div className="agent-card">
          <div className="agent-card-icon"><i className="fas fa-robot" /></div>
          <div className="agent-card-name">Agent Skill</div>
          <div className="summary-text">让 Agent 自动理解问题、查询并整理结果</div>
        </div>
        <div className="agent-card">
          <div className="agent-card-icon"><i className="fas fa-rss" /></div>
          <div className="agent-card-name">RSS</div>
          <div className="summary-text">给阅读器和低代码工具订阅更新</div>
        </div>
        <div className="agent-card">
          <div className="agent-card-icon"><i className="fas fa-code" /></div>
          <div className="agent-card-name">REST API</div>
          <div className="summary-text">给程序、工作流和自定义 Agent 使用</div>
        </div>
      </div>

      <section className="agent-section">
        <h2 className="agent-h2"><i className="fas fa-robot" />Agent Skill · 自动理解意图</h2>
        <h3 className="agent-h3">把这一段发给你的 Agent</h3>
        <p className="summary-text">
          这段提示会要求 Agent 先审阅来源、完整清单和写入位置，再原子安装到当前平台目录；不会使用 sudo，也不会留下半套新旧文件。
        </p>
        <CopyBlock code={AGENT_PROMPT}>{AGENT_PROMPT}</CopyBlock>
        <p className="summary-text agent-links">
          <a href={`${SKILL_BASE}/README.md`} target="_blank" rel="noopener noreferrer">查看完整包说明</a>
          <a href={`${SKILL_BASE}/SKILL.md`} target="_blank" rel="noopener noreferrer">先查看 SKILL.md</a>
          <a href={`${SKILL_BASE}/install.sh`} target="_blank" rel="noopener noreferrer">查看安装脚本</a>
          <a href={GITHUB_SOURCE} target="_blank" rel="noopener noreferrer"><i className="fab fa-github" /> GitHub 来源</a>
        </p>

        <h3 className="agent-h3">安装、验证、再开始使用 <span className="tag">v1.0.0</span></h3>
        <ol className="agent-steps">
          <li><strong>审阅并安装</strong><br />让 Agent 先说明来源、目标目录和写入文件，再执行安装。</li>
          <li><strong>重启或开启新会话</strong><br />大多数 Agent 只在会话开始时扫描 Skill；当前对话不一定立刻看到新文件。</li>
          <li><strong>发送验证问题</strong><br />看到明确时间窗、中文摘要和站内链接，即表示接入成功。</li>
        </ol>
        <CopyBlock code={VERIFY_QUESTION}>{VERIFY_QUESTION}</CopyBlock>
        <p className="summary-text">
          <strong>成功标志</strong>：回答注明日期窗，给出 5 条以内中文摘要与推荐理由，并把标题链接到 paper.mulanai.com 站内阅读页。
        </p>

        <details className="agent-collapse">
          <summary>
            <span className="agent-collapse-title">人类手动安装：选择当前平台</span>
            <i className="fas fa-chevron-down" />
          </summary>
          <p className="summary-text">
            安装器会先校验完整包，再一次替换目标目录。Codex、Gemini CLI、GitHub Copilot 和 OpenCode 使用同一个通用目录，装一次即可被支持该目录的多个 Agent 发现。
            如果发现 <code>~/.codex/skills/mulan-paper-skill</code>、<code>~/.gemini/skills/mulan-paper-skill</code> 等旧目录副本，追加 <code>--migrate-legacy</code> 显式迁移为共享目录单份安装。
          </p>
          <div className="platform-list">
            {PLATFORMS.map((p) => (
              <div key={p.name} className="platform-item">
                <div className="platform-head">
                  <strong>{p.name}</strong><code>{p.dir}</code>
                </div>
                <CopyBlock code={`bash <(curl -fsSL ${SKILL_BASE}/install.sh) --target ${p.target}`}>
                  {`bash <(curl -fsSL ${SKILL_BASE}/install.sh) --target ${p.target}`}
                </CopyBlock>
              </div>
            ))}
          </div>
        </details>

        <h3 className="agent-h3">没有触发？按这个顺序排查</h3>
        <ol className="agent-steps">
          <li>确认文件名严格为 <code>SKILL.md</code>，且位于当前 Agent 支持的 skills 目录。</li>
          <li>关闭旧会话并新开一个会话，再问上面的验证问题。</li>
          <li>让 Agent 列出它发现的 skills，确认其中有 <code>mulan-paper-skill</code>。</li>
          <li>仍失败时，把平台、版本和实际安装路径提交到「关于 · 反馈」页；不要发送 token 或本地隐私文件。</li>
        </ol>
      </section>

      <section className="agent-section">
        <h2 className="agent-h2"><i className="fas fa-rss" />RSS · 阅读器与自动化工具</h2>
        <p className="summary-text">
          兼容主流 RSS 2.0 阅读器。第一次接入建议选「精选摘要」；只有确实需要阅读器内全文时才选「精选全文」。
        </p>
        <div className="feed-list">
          {FEEDS.map((f) => (
            <div key={f.url} className="feed-row">
              <div className="feed-row-head">
                <strong>{f.name}</strong>
                {f.badge && <span className="tag">{f.badge}</span>}
              </div>
              <p className="summary-text">{f.desc}</p>
              <CopyBlock code={`${SITE}${f.url}`}>{`${SITE}${f.url}`}</CopyBlock>
            </div>
          ))}
        </div>
        <div className="reason-box">
          <div className="reason-head"><i className="fas fa-file-contract" />给 RSS reader / Agent 的合同</div>
          <ul className="agent-ul">
            <li>支持 ETag 条件请求；未变化时返回 304。建议每 30 分钟或更慢轮询。</li>
            <li>条目 link 指向站内阅读页，arXiv 原文入口在 description 中。</li>
            <li>默认精选与主题 feed 提供摘要，不输出 content:encoded；全文 feed 内联 markdown 正文。</li>
            <li>镜像或对外发布时请保留木兰论文解读署名和原文入口。</li>
          </ul>
        </div>
      </section>

      <section className="agent-section">
        <h2 className="agent-h2"><i className="fas fa-code" />REST API · 程序与自定义 Agent</h2>
        <p className="summary-text">
          无需 token，只允许匿名 GET。字段、错误码和 schema 以自动生成的 OpenAPI 为准。
        </p>
        <p className="summary-text agent-links">
          <a href="/openapi.json" target="_blank" rel="noopener noreferrer">打开 OpenAPI JSON</a>
          <a href="/docs" target="_blank" rel="noopener noreferrer">打开交互式文档</a>
        </p>
        <h3 className="agent-h3">首次请求</h3>
        <CopyBlock code={`curl '${SITE}${API_V1}/digests/daily?date=2026-07-24'`}>
          {`curl '${SITE}${API_V1}/digests/daily?date=2026-07-24'`}
        </CopyBlock>
        <p className="summary-text">
          浏览器、curl 与默认 SDK 都能直接读取。能设置时可以用自定义 User-Agent 自报客户端名称，方便排障，但它不是访问许可证。
        </p>
        <h3 className="agent-h3">端点速览（前缀 <code>{API_V1}</code>）</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="endpoint-table">
            <thead>
              <tr><th>方法</th><th>路径</th><th>说明</th></tr>
            </thead>
            <tbody>
              {ENDPOINTS.map((e) => (
                <tr key={e.path}>
                  <td><span className="tag">{e.method}</span></td>
                  <td><code>{e.path}</code></td>
                  <td>{e.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="agent-section">
        <h2 className="agent-h2"><i className="fas fa-shield-alt" />使用边界</h2>
        <div className="agent-cards">
          <div className="agent-card">
            <div className="agent-card-name">重要事实回原文核对</div>
            <div className="summary-text">摘要由 AI 生成。引用数字或原话前，请使用返回的 arXiv 原文 URL 复核。</div>
          </div>
          <div className="agent-card">
            <div className="agent-card-name">保留来源和署名</div>
            <div className="summary-text">对外发布时请保留木兰论文解读署名和站内链接；第三方原文版权归原作者。</div>
          </div>
          <div className="agent-card">
            <div className="agent-card-name">按频率合同调用</div>
            <div className="summary-text">API 轮询至少间隔 60 秒，RSS 建议 30 分钟或更慢；遇到限流请指数退避。</div>
          </div>
        </div>
        <p className="summary-text">
          完整边界见「关于 · 使用条款」。
        </p>
      </section>
    </div>
  );
}
